// =============================================================================
// ui/tabs/MistakeTagEditor.tsx — Settings → Preferences → mistake tags.
// -----------------------------------------------------------------------------
// The vocabulary offered on the trade form, and which of those tags make a loss
// count as CONTROLLABLE.
//
// ── ⚠ TWO COLUMNS, WRITTEN TOGETHER, BECAUSE ONE CONSTRAINS THE OTHER ──
//
// Removing a tag from the vocabulary has to prune its entry from the
// controllable map in the same write. Leave it behind and re-adding that tag
// months later brings back an opt-out the user set once and has long forgotten —
// which reads as a bug in the Controllable Losses chart, not in a settings
// screen. `pruneControllable` is applied on EVERY vocabulary commit, not only on
// a delete, because the box can be retyped wholesale.
//
// ⚠ THE MAP HOLDS OPT-OUTS ONLY. Ticking a box DELETES a key. See the domain
// header — absent means true, so a tag added on the trade form counts
// immediately without anyone visiting this screen.
// =============================================================================

import { useState } from 'react';
import { usePreferences } from '../state/preferences';
import {
  formatTagVocabulary, isControllable, parseTagVocabulary, pruneControllable,
  setControllable, type ControllableMap,
} from '../../selectors/mistakeTags';

export function MistakeTagEditor() {
  const { prefs, update } = usePreferences();
  const tags = prefs?.customMistakeTags ?? [];
  const map = (prefs?.controllableMistakeTags ?? {}) as ControllableMap;

  // ⚠ A DRAFT, committed on blur. The box is one comma-separated string for the
  // whole vocabulary, so a per-keystroke write would save a half-typed tag —
  // and `parseTagVocabulary` would turn "revenge, fo" into a real tag named
  // "fo" and prune the controllable entry of whatever it replaced.
  const [draft, setDraft] = useState(() => formatTagVocabulary(tags));
  const [seen, setSeen] = useState(() => formatTagVocabulary(tags));
  const stored = formatTagVocabulary(tags);
  if (stored !== seen) { setSeen(stored); setDraft(stored); }

  const commitVocabulary = () => {
    const next = parseTagVocabulary(draft);
    if (formatTagVocabulary(next) === stored) return;
    // ⚠ ONE PATCH, BOTH COLUMNS. Two separate writes would leave a window where
    // the vocabulary has changed and the map still names a tag that is gone.
    void update({
      customMistakeTags: next,
      controllableMistakeTags: pruneControllable(map, next),
    });
  };

  return (
    <>
      <section className="card">
        <h3>Mistake tags</h3>
        <p className="muted">
          The tags offered when you annotate a trade. Comma-separated; order is the
          order they appear on the form.
        </p>
        <label className="field">
          <span className="field-label">Vocabulary</span>
          <input
            type="text"
            aria-label="Mistake tag vocabulary"
            placeholder="revenge, chased entry, moved stop, oversized"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitVocabulary}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
          {/* ⚠ SAID OUT LOUD, because it is not recoverable from this screen:
              removing a tag here does NOT untag the trades that carry it. Beta
              behaves the same way — the tag simply stops being offered. */}
          <span className="muted field-hint">
            Removing a tag stops it being offered on new trades. Trades already
            tagged with it keep the tag.
          </span>
        </label>
      </section>

      <section className="card">
        <h3>Controllable-loss signals</h3>
        <p className="muted">
          When a loss carries any of these tags, Controllable Losses counts it.
          Untick one you consider descriptive rather than preventable — a bad fill
          you could not have avoided is not the same as chasing an entry.
        </p>
        {tags.length === 0 ? (
          <p className="muted">Add some mistake tags above to configure them here.</p>
        ) : (
          <div className="ctag-list">
            {tags.map((tag) => {
              const on = isControllable(tag, map);
              return (
                <label key={tag} className={`ctag-row${on ? ' checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => void update({
                      controllableMistakeTags: setControllable(map, tag, e.target.checked),
                    })}
                  />
                  <span className="mono ctag-name">{tag}</span>
                  <span className="muted ctag-note">treat as controllable</span>
                </label>
              );
            })}
          </div>
        )}
        {/* ⚠ NEW TAGS DEFAULT TO ON, and the screen says so rather than leaving
            it to be discovered. A user who expects the opposite would read an
            unexpectedly high Controllable Losses figure as wrong data. */}
        <p className="muted">A newly added tag counts as controllable until you untick it.</p>
      </section>
    </>
  );
}
